import React, { useState, useEffect, useRef } from 'react';
import { Loader } from '@googlemaps/js-api-loader';
import { MapPin, Search, Check } from 'lucide-react';
import api from '../../services/api';

const LocationMapSelector = ({ 
  initialAddress = '',
  initialLat = null,
  initialLng = null,
  onLocationSelect,
  apiKey = null
}) => {
  const [mapsApiKey, setMapsApiKey] = useState(apiKey);
  const [map, setMap] = useState(null);
  const [searchBox, setSearchBox] = useState(null);
  const [marker, setMarker] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState(initialAddress);
  const [selectedLocation, setSelectedLocation] = useState({
    address: initialAddress,
    lat: initialLat ? parseFloat(initialLat) : null,
    lng: initialLng ? parseFloat(initialLng) : null
  });
  const mapRef = useRef(null);
  const searchInputRef = useRef(null);

  // Fetch Google Maps API key from backend if not provided
  useEffect(() => {
    const fetchMapsApiKey = async () => {
      if (!apiKey) {
        try {
          const response = await api.get('/admin/settings');
          if (response.data.success && response.data.data.settings?.google_maps_api_key?.value) {
            setMapsApiKey(response.data.data.settings.google_maps_api_key.value);
          }
        } catch (error) {
          console.error('Failed to fetch Google Maps API key:', error);
        }
      }
    };

    fetchMapsApiKey();
  }, [apiKey]);

  // Initialize Google Maps
  useEffect(() => {
    if (!mapsApiKey || !mapRef.current) return;

    let isMounted = true;

    const initMap = async () => {
      try {
        if (!isMounted) return;
        setIsLoading(true);
        setError(null);

        // Load Google Maps API
        const loader = new Loader({
          apiKey: mapsApiKey,
          version: 'weekly',
          libraries: ['places']
        });

        const google = await loader.load();
        const { Map } = await google.maps.importLibrary("maps");
        const { Geocoder } = await google.maps.importLibrary("geocoding");

        if (!isMounted) return;

        // Default center (Mumbai)
        let center = { lat: 19.0760, lng: 72.8777 };
        
        // If we have initial coordinates, use them
        if (initialLat && initialLng) {
          center = { lat: parseFloat(initialLat), lng: parseFloat(initialLng) };
        } else if (initialAddress) {
          // Geocode the initial address
          const geocoder = new Geocoder();
          const result = await geocoder.geocode({ address: initialAddress });
          if (result.results.length > 0 && isMounted) {
            const location = result.results[0].geometry.location;
            center = { lat: location.lat(), lng: location.lng() };
          }
        }

        if (!isMounted) return;

        // Create map
        const mapInstance = new Map(mapRef.current, {
          center: center,
          zoom: 15,
          mapTypeControl: true,
          streetViewControl: true,
          fullscreenControl: true,
          zoomControl: true,
          styles: [
            {
              featureType: 'poi',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }]
            }
          ]
        });

        // Create marker
        const markerInstance = new google.maps.Marker({
          position: center,
          map: mapInstance,
          draggable: true,
          title: 'Drag to select location'
        });

        // Create search box
        const searchBoxInstance = new google.maps.places.SearchBox(searchInputRef.current);
        setSearchBox(searchBoxInstance);

        // Listen for search box selection
        searchBoxInstance.addListener('places_changed', () => {
          if (!isMounted) return;
          const places = searchBoxInstance.getPlaces();
          if (places.length === 0) return;

          const place = places[0];
          if (!place.geometry || !place.geometry.location) return;

          const location = place.geometry.location;
          const newLocation = {
            address: place.formatted_address,
            lat: location.lat(),
            lng: location.lng()
          };

          // Update marker and map
          markerInstance.setPosition(location);
          mapInstance.setCenter(location);
          mapInstance.setZoom(17);

          // Update state
          setSelectedLocation(newLocation);
          setSearchQuery(place.formatted_address);
        });

        // Listen for marker drag
        markerInstance.addListener('dragend', async () => {
          if (!isMounted) return;
          const position = markerInstance.getPosition();
          const geocoder = new Geocoder();
          
          try {
            const result = await geocoder.geocode({ 
              location: { lat: position.lat(), lng: position.lng() } 
            });
            
            if (result.results.length > 0 && isMounted) {
              const newLocation = {
                address: result.results[0].formatted_address,
                lat: position.lat(),
                lng: position.lng()
              };
              
              setSelectedLocation(newLocation);
              setSearchQuery(result.results[0].formatted_address);
            }
          } catch (error) {
            console.error('Reverse geocoding error:', error);
          }
        });

        // Listen for map click
        mapInstance.addListener('click', async (event) => {
          if (!isMounted) return;
          const geocoder = new Geocoder();
          
          try {
            const result = await geocoder.geocode({ 
              location: { lat: event.latLng.lat(), lng: event.latLng.lng() } 
            });
            
            if (result.results.length > 0 && isMounted) {
              const newLocation = {
                address: result.results[0].formatted_address,
                lat: event.latLng.lat(),
                lng: event.latLng.lng()
              };
              
              markerInstance.setPosition(event.latLng);
              setSelectedLocation(newLocation);
              setSearchQuery(result.results[0].formatted_address);
            }
          } catch (error) {
            console.error('Reverse geocoding error:', error);
          }
        });

        if (isMounted) {
          setMap(mapInstance);
          setMarker(markerInstance);
          setIsLoading(false);
        }

      } catch (error) {
        if (isMounted) {
          console.error('Google Maps initialization error:', error);
          setError(error.message);
          setIsLoading(false);
        }
      }
    };

    initMap();

    return () => {
      isMounted = false;
    };
  }, [mapsApiKey]); // Only depend on mapsApiKey to prevent infinite loops

  const handleLocationSelect = () => {
    if (onLocationSelect) {
      onLocationSelect(selectedLocation);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchBox && searchQuery.trim()) {
      searchBox.setBounds(map.getBounds());
    }
  };

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-600">Error loading map: {error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search Input */}
      <form onSubmit={handleSearchSubmit} className="flex space-x-2">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search for an address..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <button
          type="submit"
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          Search
        </button>
      </form>

      {/* Map */}
      <div className="relative" style={{ height: '400px' }}>
        {isLoading && (
          <div className="absolute inset-0 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center z-10">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Loading map...</p>
            </div>
          </div>
        )}
        
        <div ref={mapRef} className="w-full h-full rounded-lg border border-gray-300 dark:border-gray-600" />
      </div>

      {/* Selected Location Info */}
      {selectedLocation.address && (
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 dark:text-white mb-2 flex items-center">
            <MapPin className="w-4 h-4 mr-2" />
            Selected Location
          </h4>
          <div className="space-y-2 text-sm">
            <div>
              <span className="font-medium text-gray-700 dark:text-gray-300">Address:</span>
              <span className="ml-2 text-gray-600 dark:text-gray-400">{selectedLocation.address}</span>
            </div>
                         <div>
               <span className="font-medium text-gray-700 dark:text-gray-300">Latitude:</span>
               <span className="ml-2 text-gray-600 dark:text-gray-400">
                 {typeof selectedLocation.lat === 'number' ? selectedLocation.lat.toFixed(6) : selectedLocation.lat || 'Not set'}
               </span>
             </div>
             <div>
               <span className="font-medium text-gray-700 dark:text-gray-300">Longitude:</span>
               <span className="ml-2 text-gray-600 dark:text-gray-400">
                 {typeof selectedLocation.lng === 'number' ? selectedLocation.lng.toFixed(6) : selectedLocation.lng || 'Not set'}
               </span>
             </div>
          </div>
          <button
            onClick={handleLocationSelect}
            className="mt-3 inline-flex items-center px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 text-sm"
          >
            <Check className="w-4 h-4 mr-2" />
            Use This Location
          </button>
        </div>
      )}

      <div className="text-xs text-gray-500 dark:text-gray-400">
        💡 Tip: You can search for an address, click on the map, or drag the marker to select a location.
      </div>
    </div>
  );
};

export default LocationMapSelector;
